import axios from 'axios';
import { backend_server_address } from '../backendServer';
import { isAndroid, isIOS } from './platform';
import { isInAppPath } from './pushPayload';

/**
 * Records that a person tapped a notification banner (`notification-build-stages.md` S6F).
 *
 * Engagement is product analytics — which channels people respond to, which types get
 * ignored at which plants. It is NOT a read state: read belongs to the thing notified
 * about (`notification-experience.md` §6), and this call never writes one. The server
 * side is append-only with no update path, so a retry costs a near-duplicate row rather
 * than corrupting anything, which is why this is safe to fire without confirmation.
 *
 * It runs in JS rather than native so a notifications-inbox UI can reuse the same call
 * later, and it deliberately does NOT block routing: the tap's job is to put the person
 * where the notification pointed, and an analytics write must never delay or prevent it.
 * Hence the void return and the swallowed failure — a caller cannot await this by
 * accident, and a dead network cannot strand the user on a banner that did nothing.
 *
 * Chat is not recorded here. Its engagement has a different shape (`conversation_id` and
 * the tapped banner's `seq`, not a delivery id) and rides chat's own branch; a chat
 * payload simply carries no `delivery_id`, so the guard below skips it by construction.
 */

/**
 * Where the endpoint sits when the payload does not say. The server puts its own reverse()
 * of the same view in `data.engagement_url`, so this is the floor for a push that predates
 * that field rather than a second source of truth — the day the route moves, the payload
 * carries the new one and this constant stops being reached.
 */
const DEFAULT_ENGAGEMENT_PATH = '/notifications/engagement/';

/** Paths arrive with `/api` stripped, because that is the prefix every caller here adds. */
const engagementUrl = (data: Record<string, unknown>): string =>
    `${backend_server_address}/api${isInAppPath(data.engagement_url) ? data.engagement_url : DEFAULT_ENGAGEMENT_PATH}`;

/** The `PlatformEnum` the endpoint validates against (login/models.py) has these two only. */
const platformName = (): 'android' | 'ios' | null => (isAndroid() ? 'android' : isIOS() ? 'ios' : null);

/**
 * FCM data values are always strings, and the endpoint parses the id as an integer;
 * anything else is a payload we did not send, so it is dropped rather than posted.
 */
const asDeliveryId = (value: unknown): string | null =>
    typeof value === 'string' && /^[1-9]\d*$/.test(value) ? value : null;

export const recordNotificationEngagement = (data: Record<string, unknown> | undefined): void => {
    if (!data) return;

    const deliveryId = asDeliveryId(data.delivery_id);
    if (deliveryId === null) return;

    const platform = platformName();
    if (platform === null) return;

    // No await anywhere on this path: the caller routes immediately, and the promise
    // settles on its own. The catch is what keeps a failed POST from surfacing as an
    // unhandled rejection in a listener nobody is watching.
    axios
        .post(engagementUrl(data), { delivery_id: deliveryId, platform }, { withCredentials: true })
        .catch(() => undefined);
};
